import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { AuthUser } from '../../types';
import { MODULES, MODULE_CATEGORIES, SETTINGS_TILE } from '../../utils/constants';
import { hasModuleAccess, isAdmin as checkAdmin } from '../../utils/auth';
import { useChurchConfig } from '../../hooks';
import { parseHiddenModules, parseModuleTitles, parseCategoryOrder, parseModuleOrder, sortCategories, sortModules } from '../../utils/churchSettings';
import { moduleIdForRoute, SETTINGS_MODULE_ID, SETTINGS_ROUTE, isSettingsSection } from '../../routes';
import type { SettingsSection } from '../../routes';

// The menu, whichever way it is drawn (sidebar or top bar): the modules this
// user may open, grouped by category, minus the ones the parish hid under
// Settings › Modules, with any tile titles it renamed there, categories and
// modules in the order it numbered them. Also which entry is current, from
// the URL.

export interface NavItem {
  id: string;
  route: string;
  title: string;
  icon: string;
  path: string;
}

export interface NavGroup {
  category: string;
  items: NavItem[];
}

export interface NavModel {
  groups: NavGroup[];
  /** The Settings entry, or null for a user who is not an administrator. */
  settings: NavItem | null;
  atHome: boolean;
  /** MODULES id (or 'Settings') of the open screen, if any. */
  activeModuleId?: string;
  /** Open Settings section, when the Settings screen is up. */
  activeSection?: SettingsSection;
}

export const useNavItems = (user: AuthUser | null): NavModel => {
  const config = useChurchConfig();
  const { pathname } = useLocation();

  const groups = useMemo<NavGroup[]>(() => {
    const hidden = new Set(parseHiddenModules(config));
    const titles = parseModuleTitles(config);
    const ordered = sortModules(MODULES, parseModuleOrder(config));
    return sortCategories(MODULE_CATEGORIES, parseCategoryOrder(config))
      .map((category) => ({
        category,
        items: ordered
          .filter((m) => m.category === category && hasModuleAccess(user, m.id) && !hidden.has(m.id))
          .map((m) => ({
            id: m.id, route: m.route, icon: m.icon,
            title: titles[m.id] || m.title,
            path: `/${m.route}`
          }))
      }))
      .filter((g) => g.items.length > 0);
  }, [config, user]);

  const settings = useMemo<NavItem | null>(() => (checkAdmin(user)
    ? { id: SETTINGS_TILE.id, route: SETTINGS_ROUTE, icon: SETTINGS_TILE.icon, title: SETTINGS_TILE.title, path: `/${SETTINGS_ROUTE}` }
    : null), [user]);

  const [first = '', second = ''] = pathname.split('/').filter(Boolean);
  const activeModuleId = moduleIdForRoute(first);

  return {
    groups,
    settings,
    atHome: pathname === '/',
    activeModuleId,
    activeSection: activeModuleId === SETTINGS_MODULE_ID && isSettingsSection(second) ? second : undefined
  };
};

export default useNavItems;
